import React, { useContext, useEffect, useState } from 'react';
import { Modal, Progress, Spin, Icon } from 'antd';
import AppContext from '../../context/app.context';
import { IAppContext } from '../../interfaces/context.interfaces';
import { ITimelapseFrame } from '../../services/svidashboard-image.util';
import { useTimelapsePreload } from '../../hooks/useTimelapsePreload';
import styles from './TimelapseModal.module.css';

interface ITimelapseModalProps {
  visible: boolean;
  objectId: string;
  onClose: () => void;
}

type FetchPhase = 'idle' | 'fetching' | 'done' | 'error';

const TimelapseModal: React.FC<ITimelapseModalProps> = ({
  visible,
  objectId,
  onClose,
}) => {
  const appContext = useContext<IAppContext>(AppContext);
  const [frames, setFrames] = useState<ITimelapseFrame[]>([]);
  const [fetchPhase, setFetchPhase] = useState<FetchPhase>('idle');
  const [fetchError, setFetchError] = useState<string | null>(null);

  const {
    phase,
    progressPercent,
    loadedCount,
    totalCount,
    playableFrames,
    currentIndex,
    errorMessage,
    handlePrevious,
    handleNext,
  } = useTimelapsePreload({
    frames,
    objectId,
    enabled: visible && fetchPhase === 'done' && frames.length > 0,
  });

  useEffect(() => {
    if (!visible) {
      setFrames([]);
      setFetchPhase('idle');
      setFetchError(null);
      return;
    }

    let cancelled = false;
    const services = appContext?.services;

    const fetchFrames = async () => {
      if (!services?.sviDashboardService) {
        if (!cancelled) {
          setFetchPhase('error');
          setFetchError('Dashboard service is not available');
        }
        return;
      }

      setFetchPhase('fetching');
      setFetchError(null);
      setFrames([]);

      try {
        const response = await services.sviDashboardService.getTimelapseImages(
          objectId,
        );
        const data = response.data;

        if (cancelled) {
          return;
        }

        if (!data?.success || !data.frames?.length) {
          setFetchPhase('error');
          setFetchError(
            data?.message || 'No timelapse images found for this object',
          );
          return;
        }

        setFrames(data.frames);
        setFetchPhase('done');
      } catch {
        if (!cancelled) {
          setFetchPhase('error');
          setFetchError('Failed to load timelapse images');
        }
      }
    };

    void fetchFrames();

    return () => {
      cancelled = true;
    };
  }, [appContext?.services, objectId, visible]);

  const currentFrame = playableFrames[currentIndex];
  const showPreloadBar = fetchPhase === 'done' && phase === 'preloading';
  const showViewer =
    fetchPhase === 'done' &&
    (phase === 'playing' || phase === 'manual') &&
    currentFrame;
  const showControls = showViewer && playableFrames.length > 1;
  const displayError = fetchError || (phase === 'error' ? errorMessage : null);

  return (
    <Modal
      visible={visible}
      title="Timelapse"
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      {fetchPhase === 'fetching' && (
        <div className={styles.loadingSection}>
          <Spin />
          <p className={styles.loadingLabel}>Loading timelapse…</p>
        </div>
      )}

      {displayError && (
        <div className={styles.errorMessage}>{displayError}</div>
      )}

      {showPreloadBar && (
        <div className={styles.loadingSection}>
          <p className={styles.loadingLabel}>
            Loading images {loadedCount} of {totalCount}
          </p>
          <Progress
            className={styles.progressBar}
            percent={progressPercent}
            status={progressPercent < 100 ? 'active' : 'success'}
            showInfo
            strokeWidth={12}
          />
        </div>
      )}

      {showViewer && currentFrame && (
        <>
          <div className={styles.imageSection}>
            <img
              src={currentFrame.src}
              alt={currentFrame.title}
              className={styles.frameImage}
            />
            <div className={styles.frameOverlay}>
              <span className={styles.frameTitle}>{currentFrame.title}</span>
              {currentFrame.date ? (
                <span className={styles.frameDate}>{currentFrame.date}</span>
              ) : null}
            </div>
          </div>

          {showControls && (
            <div className={styles.controls}>
              <button
                type="button"
                className={styles.navButton}
                onClick={handlePrevious}
                aria-label="Previous frame"
              >
                <Icon type="left" />
              </button>
              <button
                type="button"
                className={styles.navButton}
                onClick={handleNext}
                aria-label="Next frame"
              >
                <Icon type="right" />
              </button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default TimelapseModal;
